export interface HelpEntry {
  label: string;
  text: string;
  example?: string;
  docsUrl?: string;
}

export const STRATEGY_HELP: Record<string, HelpEntry> = {
  // Strategy-level
  moveSlToCost: {
    label: 'Move SL to Cost',
    text: 'Multi-leg safeguard: when one leg\'s stop-loss hits, every other open leg that\'s currently in profit has its stop-loss pulled to its own entry price — locking in at-least-breakeven on the survivors so a reversal can\'t turn their gains into a loss. Strategy-level (all legs); a leg still in loss is left untouched. Requires a stop-loss on the legs. Works with UL (underlying) stop-losses too — a UL SL moves to the leg\'s entry spot.',
    example: 'Short strangle — the CE leg\'s SL hits (loss). PE (sold ₹100, now ₹70, in profit) has its SL pulled ₹150 → ₹100 (entry) → a reversal exits PE at breakeven, not a loss.',
    docsUrl: '/help/move-sl-to-cost.html',
  },
  rangeBreakout: {
    label: 'Range Breakout (ORB)',
    text: 'Enables Opening Range Breakout. A price range is formed between the Range Start and Range End times. Your strategy only triggers when price breaks out of that range.',
    example: 'Range 9:15–9:30 forms ₹100–₹110 band → entry triggers only on breakout above/below',
    docsUrl: '/help/range-breakout.html',
  },
  entryTime: {
    label: 'Entry Time',
    text: 'The time from which entries are allowed. If Range Breakout is enabled, this is also the Range Start — the beginning of the range-building window.',
    example: 'Set to 9:15 AM → no entries placed before this time',
  },
  rangeEndTime: {
    label: 'Range End Time',
    text: 'The time at which the opening range window closes. Breakout conditions are evaluated only after this time.',
    example: 'Range End 9:30 AM → breakout signals checked from 9:30 AM onwards',
  },
  exitTime: {
    label: 'Exit Time',
    text: 'Hard exit time. All open positions are squared off at or after this time, regardless of P&L.',
    example: 'Set to 3:15 PM → all legs closed at 3:15 PM even if in profit',
  },
  tradingType: {
    label: 'Trading Type',
    text: 'Intraday trades within a single session — the Exit Mode then decides day-end behaviour: Same Day squares off before market close, while Next Day (BTST/STBT) carries the position overnight to exit the next trading day. Positional holds across multiple days per the trading-days-before-expiry settings.',
    example: 'Intraday + Same Day → exit 3:15 PM today. Intraday + Next Day → carried overnight, exit next trading day. Positional → spans multiple sessions.',
  },
  positional: {
    label: 'Positional',
    text: 'The strategy can hold positions across multiple days based on trading-days-before-expiry settings.',
    example: 'Enter 5 days before expiry, exit 1 day before expiry — spans multiple sessions',
  },
  intradayExitMode: {
    label: 'Exit Mode',
    text: 'Same Day closes all positions before market close. Next Day (BTST/STBT) allows holding overnight and exiting the following trading day.',
    example: 'BTST: Buy today, sell tomorrow. STBT: Sell today, buy tomorrow.',
  },
  expiryDayRollover: {
    label: 'Continue on expiry days',
    text: 'When the underlying\'s current contract expires today, place the entry against the next contract so BTST/STBT still runs. For Next Weekly / Next Monthly, also rolls the running chain forward when the locked contract reaches its own expiry day and the chain would carry overnight.',
    example: 'NIFTY weekly expiry on Thu → entry trades next Thu\'s contract. Next Weekly + enabled: if a multi-day chain is still running when that next-Thu contract expires, the chain rolls to the following week automatically.',
  },
  expiryType: {
    label: 'Expiry Type',
    text: 'Which contract this strategy trades. Weekly / Monthly = the current cycle. Next Weekly / Next Monthly = the cycle after that — every entry on every day. Monthly-only underlyings (BANKNIFTY, FINNIFTY, MIDCPNIFTY, BANKEX) only allow Monthly / Next Monthly.',
    example: 'NIFTY + Next Weekly + entry 4 trading days before expiry → fires 4 trading days before next week\'s Tuesday expiry, against next week\'s contract.',
  },
  tradingDaysBeforeExpiryEntry: {
    label: 'Entry Days Before Expiry',
    text: 'For positional strategies — how many trading days before expiry to enter. Weekends and market holidays are excluded from the count.',
    example: 'Value = 5 → enters on the 5th trading day before expiry',
  },
  tradingDaysBeforeExpiryExit: {
    label: 'Exit Days Before Expiry',
    text: 'For positional strategies — how many trading days before expiry to exit. Set lower than entry days to define a holding window.',
    example: 'Entry = 5 days, Exit = 1 day → hold for 4 trading days',
  },
  strategyTargetProfit: {
    label: 'Strategy Target Profit',
    text: 'An overall P&L target across all legs combined. When the total MTM profit across all positions hits this value, the entire strategy exits.',
    example: 'Target = ₹5,000 → all legs squared off when combined profit reaches ₹5,000',
    docsUrl: '/help/strategy-target-profit.html',
  },
  strategyStopLoss: {
    label: 'Strategy Stop Loss',
    text: 'An overall P&L stop loss across all legs combined. When the total MTM loss hits this value, the entire strategy exits regardless of individual legs.',
    example: 'SL = ₹3,000 → all legs squared off when combined loss reaches ₹3,000',
    docsUrl: '/help/strategy-stop-loss.html',
  },

  // Position-level
  optionType: {
    label: 'Option Type',
    text: 'CE (Call option) profits when price rises. PE (Put option) profits when price falls. Choose based on your market direction view.',
    example: 'Bullish view → buy CE. Bearish view → buy PE.',
  },
  action: {
    label: 'Action (Buy / Sell)',
    text: 'BUY pays a premium upfront with limited risk and unlimited potential. SELL receives premium upfront but carries higher risk if the move goes against you.',
    example: 'Sell CE @ ₹80 → collect ₹80 premium. Max gain = ₹80 if CE expires worthless.',
  },
  totalLots: {
    label: 'Total Lots',
    text: 'Number of lots for this position. One lot = the exchange-defined lot size (NIFTY=65, BANKNIFTY=15, SENSEX=10). Large lot counts are split into multiple broker orders to respect exchange freeze limits — e.g. 100 NIFTY lots = ~4 broker orders per allocation.',
    example: 'NIFTY lot size = 65. 2 lots = 130 units. ₹1 move = ₹130 P&L.',
  },
  strikeSelection: {
    label: 'Strike Selection',
    text: 'How the strike price is chosen at entry time: ATM ± offset, by premium amount, or as a percentage of the straddle premium.',
    example: 'ATM+2 → selects strike 2 steps above the current ATM. Premium = ₹50 → selects strike closest to ₹50.',
    docsUrl: '/help/strike-selection.html',
  },

  // Risk management (leg-level)
  stopLoss: {
    label: 'Stop Loss',
    text: 'Automatically exits this leg if the premium moves against you by the set amount. Enabling Stop Loss also unlocks Trailing SL and Re-Entry for this leg.',
    example: 'Sold CE @ ₹50 · SL = 50% → exits if premium rises to ₹75',
    docsUrl: '/help/stop-loss.html',
  },
  reinstateOvernight: {
    label: 'Continue SL Overnight',
    text: 'Controls whether the stop-loss order is re-placed the next morning after market close. When ON (default), EOD cancels the SL and SOD re-places it at 09:18. When OFF, the SL is cleared at EOD and not re-placed — useful when your exit time is at market open, avoiding a race between SL and exit orders. For a UL (underlying) stop-loss no order is involved: OFF means the UL SL (and a UL trailing SL) isn\'t watched on any day after the entry day — a UL target still applies, and the scheduled exit fires.',
    example: 'Exit at 09:18 + Continue OFF → no SL next morning, only the scheduled exit fires',
  },
  trailingStopLoss: {
    label: 'Trailing Stop Loss',
    text: 'Ratchets the Stop Loss trigger toward entry as the premium moves in your favour. For every "instrument moves" amount of favourable movement, the SL trigger advances by "move SL by". Anchored to the SL trigger captured at first placement; only ever moves SL the favourable direction. Requires a valid Stop Loss. A UL (underlying) stop-loss trails with a UL type, in index points / %.',
    example: 'SELL @ ₹150 · SL at ₹165 · trail 10 pts / move 5 pts → premium drops to ₹130 (20 pts profit = 2 steps) → SL moves to ₹155',
    docsUrl: '/help/trailing-stop-loss.html',
  },
  reEntry: {
    label: 'Re-Entry (candle)',
    text: 'After Stop Loss hits, waits for a 1-minute candle to close back past the original entry price before re-entering. On confirmation the broker places a LIMIT at the original price. Counter-bounded (1–5). Retries reuse the original strike and price — not today\'s ATM. Requires Stop Loss.',
    example: 'SELL @ ₹150 · SL at ₹190.5 hits · Re-Entry count = 2 → next 1-min close ≤ ₹150 triggers a fresh LIMIT SELL @ ₹150 · up to 2 retries',
    docsUrl: '/help/re-entry.html',
  },
  reCost: {
    label: 'Re-Cost (immediate)',
    text: 'After Stop Loss hits, places an SL-trigger order at the original entry price right away; the broker fills it only when price returns to that level. With an underlying (UL) stop-loss it instead re-enters at the current price once the index crosses back past the entry spot. On a UL wait-and-trade leg with "First entry only" off, that cross-back (the premium back through the entry price for a premium stop-loss, the index back past the entry spot for a UL one) re-runs the spot wait instead of entering. Counter-bounded (1–5). Retries reuse the original strike — not today\'s ATM — and, with a premium stop-loss, the original price. Requires Stop Loss.',
    example: 'SELL @ ₹150 · SL hits · Re-Cost count = 2 → SL-trigger SELL @ ₹150 placed right away; fills when premium returns to ₹150 · up to 2 retries',
    docsUrl: '/help/re-cost.html',
  },
  reExec: {
    label: 'Re-Execute (fresh)',
    text: 'After Stop Loss hits, immediately re-runs the leg\'s strike selection against the current market and places a fresh LIMIT at the new strike\'s LTP. Honours your full leg config — strike rule, stop loss percent, etc. — as if entry fired right now. Counter-bounded (1–5). Best for "stay in this leg all session" strategies. Requires Stop Loss.',
    example: 'SELL ATM CE · NIFTY 24000, sold 24000 CE @ ₹150 · SL hit, NIFTY now 24150 → Re-Exec resolves fresh ATM 24150, SELL 24150 CE @ ~₹148, new SL from leg config × ₹148',
    docsUrl: '/help/re-execute-sl.html',
  },
  targetProfit: {
    label: 'Target Profit',
    text: 'Automatically exits this leg when the premium reaches your profit target. Enabling Target Profit also unlocks Re-Execute for this leg.',
    example: 'Sold CE @ ₹50 · TP = 60% → exits when premium falls to ₹20',
    docsUrl: '/help/target-profit.html',
  },
  reExecute: {
    label: 'Re-Execute',
    text: 'After Target Profit is hit and the leg exits, Re-Execute places the same leg again — up to the configured count. Requires a valid Target Profit.',
    example: 'TP hit · Re-Execute = 2 → leg re-enters up to 2 more times after each target',
    docsUrl: '/help/re-execute.html',
  },
  hedgeRecycle: {
    label: 'Recycle hedge',
    text: 'When ON, the hedge is squared as soon as its main leg exits early — on stop-loss, trailing-stop-loss, or target-profit — instead of lingering as a lone protective long until the strategy\'s scheduled exit. If the main re-enters, a fresh hedge is bought at the new strike for each re-entered chunk, so protection tracks the current strike. When OFF (default), the original hedge is kept until the strategy exits. Available when the main has a stop-loss, trailing-stop-loss, target-profit, re-entry, or re-execute enabled.',
    example: 'Main stop-loss hits (no re-entry) → hedge squared the same cycle instead of held to EOD; if the main re-enters at a new strike → fresh hedge bought there',
  },
  waitAndTrade: {
    label: 'Wait & Trade',
    text: 'Waits for the option premium (LTP of this leg) to move by your set amount before executing. Enters immediately if the condition is already met — otherwise places a pending order with the broker that auto-fills when the trigger premium is reached.',
    example: 'Option premium = ₹100 · Wait = 10 pts (SELL) → trigger at ₹90. Fills when premium drops there.',
    docsUrl: '/help/wait-and-trade.html',
  },
  continueOvernight: {
    label: 'Continue Overnight',
    text: 'Controls whether an unfilled wait-and-trade entry order is re-placed the next morning. When ON, EOD preserves the trigger price and SOD re-places the entry SL order at 09:18. When OFF (default), unfilled entries are closed at EOD with no P&L.',
    example: 'WAT entry not filled by 15:54 + Continue ON → same trigger order placed next morning at 09:18',
  },
  continueOvernightOrbDisabled: {
    label: 'Continue Overnight — unavailable',
    text: 'Disabled because Range Breakout (ORB) is enabled on this strategy. With ORB, the opening-range breakout controls each day\'s entry. To carry an unfilled ORB SL-trigger entry across days, enable the ORB "Carry forward to next day" toggle in Schedule & Risk instead.',
    example: 'Range Breakout ON → use the ORB carry-forward toggle to hold the breakout trigger overnight.',
  },
  orbContinueOvernight: {
    label: 'Carry forward to next day',
    text: 'If the opening-range breakout never triggers today, the SL-trigger entry order is preserved and re-placed at the same trigger price every trading day until it fills or you pause the strategy — including days outside the configured entry days. Requires NRML product on a Positional or BTST (next-day exit) strategy; not available for same-day intraday since entry and exit run within the same trading day.',
    example: 'BUY ORB at range_high=110, market closes at 108 → next morning, same trigger re-armed at 110.',
  },
  tradeOnlyFirstEntry: {
    label: 'First entry only',
    text: 'Applies to UL wait-and-trade legs on re-entry. When OFF (default), each SL Re-entry and SL Re-cost re-runs the spot wait — a fresh invisible-wait row is written and the child leg does not enter until the underlying moves past the reference again in the configured direction. A re-entry still waiting at market close carries to the next day only if the W&T "Overnight" box is ticked; otherwise it is closed and the leg makes no further re-entries. When ON, the spot wait is skipped: SL Re-entry (and SL Re-cost on a UL stop-loss) enters on the cross-back at current market, SL Re-cost on a premium stop-loss re-enters at cost through a broker stop, and re-execute enters a fresh strike straight away. SL Re-execute and Re-execute on target can\'t re-run the wait, so they need this ON.',
    example: 'UL W&T UP 50 pts fills → SL hits → OFF: child waits again until spot climbs 50 pts from the new reference; ON: child enters immediately when spot crosses back.',
    docsUrl: '/help/underlying-based-risk.html',
  },
};
